import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Checkbox, Form, Input, Modal, Select, Radio, Spin } from 'antd';
import { useAppNavigate, usePermission } from '@/hooks';
import SystemHeader from '@/widgets/layout/SystemHeader';
import {
  APP_DOC_ATTACHMENT_GROUPS,
  APP_DOC_ADDON_TYPES,
  normalizeAddonCategory,
  APP_DOC_RELATION_OPTIONS,
  appDocAddonMeta,
  attachmentFilesOf,
  isMultiFileAttachmentKey,
  normalizeAttachmentsForForm,
} from '@/widgets/application/appDocContent';
import { applicationDocsApi } from '@/services/applicationDocs/api';
import { recipientsApi } from '@/services/recipients/api';
import type { ApplicationDocAttachments, ApplicationDocCreateRequest, ApplicationDocResponse, ApplicationDocUpdateRequest } from '@/services/applicationDocs/types';
import { isHandledAuthError } from '@/services/client';
import { useToast } from '@/widgets/common';
import { joinJapaneseName, normalizeJapaneseName, splitJapaneseName } from '@/utils/applicationDoc';
import { caseStatusKeyByLabel, caseStatusPermissions } from '@/constants/enums';
import {
  APPLICATION_BANK_ACCOUNT_TYPE_OPTIONS,
  normalizeApplicationBankAccountType,
} from '@/utils/bankAccountType';
import { normalizePastAddressPeriodMonths } from '@/utils/pastAddressPeriod';
import {
  bankAccountHolderKanaError,
  bankAccountNumberError,
  bankBranchCodeError,
  bankCodeError,
  digitsOnly,
  sanitizeBankFieldInput,
} from '@/utils/bankInfoValidation';
import type { AppDocDataLike } from './ApplicationDocDetail';

interface ApplicationDocEditProps {
  data: AppDocDataLike;
  setData: (data: AppDocDataLike) => void;
  returnPage?: string;
}

interface FormMember {
  furigana: string;
  name: string;
  phone: string;
  email: string;
  relationToHead: string;
  relationToHeadOther: string;
  birthDate: string;
  benefitPeriodStart: string;
  benefitPeriodEnd: string;
  death: string;
  benefitPeriod?: string;
  [key: string]: string | undefined;
}

interface FormPastAddress {
  address: string;
  period: string;
  periodStartMonth: string;
  periodEndMonth: string;
  [key: string]: string;
}
interface FormOtherMember { name: string; phone: string; email: string; [key: string]: string }
interface FormAdditionRow {
  category: string;
  amount: string;
  name: string;
  periodStartMonth: string;
  periodEndMonth: string;
  facility: string;
  evidenceFileName?: string;
  evidenceKey?: string;
  [key: string]: string | undefined;
}
interface FormBank {
  accountHolderKana: string;
  bankCode: string;
  bankBranchCode: string;
  accountType: string;
  accountNumber: string;
  [key: string]: string;
}

interface FormState {
  identId: string;
  id: string | number;
  applicationNo: string;
  name: string;
  // 申出者フリガナ・生年月日（検討事項 No.66）。従来は世帯構成1行目から導出していたが、
  // 申出者が世帯主とは限らないため独立した項目として保持する。
  applicantFurigana: string;
  applicantBirthDate: string;
  applicantPhone: string;
  applicantEmail: string;
  currentAddress: string;
  applicationDate: string;
  caseStatus: string;
  householdNo: string;
  members: FormMember[];
  protectionResidenceSame: 'same' | 'different';
  pastAddresses: FormPastAddress[];
  bank: FormBank;
  otherHouseholdMembers: FormOtherMember[];
  additionRows: FormAdditionRow[];
  attachments: ApplicationDocAttachments;
  memo: string;
}

type MemberErrors = Record<string, string>;

type AppDocCoreSubmitErrors = {
  householdNo?: string;
  name?: string;
  applicantName?: string;
  applicantFurigana?: string;
  applicantBirthDate?: string;
  applicantPhone?: string;
  applicantEmail?: string;
  currentAddress?: string;
  applicationDate?: string;
  members?: Record<number, MemberErrors>;
  bank?: Record<string, string>;
  otherHouseholdMembers?: Record<number, Record<string, string>>;
  attachments?: string;
  missingAttachmentKeys?: string[];
};

function parsePositiveInt(value: string | undefined | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-red-500 text-sm font-bold">*</span>
    <span>{label}</span>
  </span>
);

// version2 のセクション見出し（番号バッジ／青バー＋下線）。表示専用。
type SectionHeaderProps = { badge?: string; title: string };
const SectionHeader = ({ badge, title }: SectionHeaderProps) => (
  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
    {badge ? (
      <span className="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white text-sm font-bold shadow-sm">
        {badge}
      </span>
    ) : (
      <span className="inline-block w-1.5 h-5 rounded-full bg-blue-600 shrink-0" />
    )}
    <h3 className="text-base font-bold text-gray-800">{title}</h3>
  </div>
);

type NameSplitInputProps = {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  lastLabel?: string;
  firstLabel?: string;
  lastPlaceholder?: string;
  firstPlaceholder?: string;
  lastAriaLabel?: string;
  firstAriaLabel?: string;
};

const NameSplitInput = ({
  value,
  onChange,
  disabled = false,
  lastLabel = '姓',
  firstLabel = '名',
  lastPlaceholder = '例：山田',
  firstPlaceholder = '例：太郎',
  lastAriaLabel = '姓',
  firstAriaLabel = '名',
}: NameSplitInputProps) => {
  const { lastName, firstName } = splitJapaneseName(value);
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700 shrink-0">{lastLabel}：</span>
        <Input
          type="text"
          value={lastName}
          onChange={(e) => onChange(joinJapaneseName(e.target.value, firstName))}
          placeholder={lastPlaceholder}
          aria-label={lastAriaLabel}
          disabled={disabled}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700 shrink-0">{firstLabel}：</span>
        <Input
          type="text"
          value={firstName}
          onChange={(e) => onChange(joinJapaneseName(lastName, e.target.value))}
          placeholder={firstPlaceholder}
          aria-label={firstAriaLabel}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const ATTACHMENT_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ALLOWED_ATTACHMENT_FILE_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png']);
// 添付 1 ファイルあたりの上限。住民入力フォーム／バックエンドの上限と同値に保つこと（2026-08-06 に 7MB へ変更）。
const MAX_ATTACHMENT_FILE_SIZE_BYTES = 7 * 1024 * 1024;
const MAX_FILES_PER_ATTACHMENT = 10;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REQUIRED_ID_ATTACHMENT_KEYS = [
  'mynumber',
  'mynumberBack',
  'license',
  'licenseBack',
  'residenceCard',
  'residenceCardBack',
  'disabilityBook',
  'disabilityBookBack',
  'passport',
];

const ATTACHMENT_REQUIRED_MESSAGES: Record<string, string> = {
  kosekiAll: '全世帯員の戸籍謄本の写し（全部事項証明書）を添付してください。',
  bankBookOrCard: '申出者本人の振込先口座が確認できる通帳またはキャッシュカードの写しを添付してください。',
};

const validateAttachmentFile = (file: File): string | null => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_ATTACHMENT_FILE_EXTENSIONS.has(extension)) {
    return '添付ファイルはPDF、JPG、JPEG、PNG形式を選択してください。';
  }
  if (file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES) {
    return '添付ファイルは1ファイル7MB以下を選択してください。';
  }
  return null;
};

const emptyMember = (): FormMember => ({
  furigana: '',
  name: '',
  phone: '',
  email: '',
  relationToHead: '',
  relationToHeadOther: '',
  birthDate: '',
  benefitPeriodStart: '',
  benefitPeriodEnd: '',
  death: '',
});

const emptyAddition = (): FormAdditionRow => ({
  category: APP_DOC_ADDON_TYPES[0],
  amount: '',
  name: '',
  periodStartMonth: '',
  periodEndMonth: '',
  facility: '',
});

const splitBenefitPeriod = (combined: string) => {
  const parts = String(combined ?? '').replace('～', '~').split('~', 2);
  return { benefitPeriodStart: (parts[0] ?? '').trim(), benefitPeriodEnd: (parts[1] ?? '').trim() };
};

const toResidenceMode = (value: unknown): 'same' | 'different' => {
  if (value === false || value === 'different') return 'different';
  return 'same';
};

const isValidFurigana = (value: string) => /^[ァ-ヶヷ-ヺー・\u3000 ]+$/.test(value.trim());

const furiganaSplitErrorMessage = (value: unknown) => {
  const { lastName, firstName } = splitJapaneseName(value);
  const lastKana = String(lastName || '').trim();
  const firstKana = String(firstName || '').trim();
  if (!lastKana && !firstKana) return 'フリガナを入力してください。';
  if ((lastKana && !isValidFurigana(lastKana)) || (firstKana && !isValidFurigana(firstKana))) {
    return 'フリガナは全角カタカナで入力してください。';
  }
  return '';
};

const phoneDigitsOnly = (value: unknown) => digitsOnly(String(value ?? '').replace(/-/g, ''));

const isValidPhoneNumber = (value: string) => {
  const digits = phoneDigitsOnly(value);
  return /^\d{10,11}$/.test(digits);
};

const parseComparableDate = (value: unknown): number | null => {
  const normalized = String(value ?? '').trim().replace(/\//g, '-');
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date.getTime();
};

const mergeForm = (src: AppDocDataLike | null | undefined): FormState => {
  const d = (src ?? {}) as AppDocDataLike;
  const residenceMode = toResidenceMode(d.protectionResidenceSame);
  return {
    identId: d.identId ?? '',
    id: d.id ?? '',
    applicationNo: d.applicationNo ?? '',
    name: normalizeJapaneseName(d.applicantName ?? d.name),
    // 申出者フリガナが未設定の既存データは、従来の保持先だった世帯構成1行目のフリガナで補う。
    // バックエンドは未設定を空文字で返すため、?? ではなく || でフォールバックする。
    // 申出書OCR取込の案件は applicantFurigana を持たないため、従来の保持先だった
    // 世帯構成1行目のフリガナで補う。
    applicantFurigana: normalizeJapaneseName(
      d.applicantFurigana
        || (Array.isArray(d.members) ? (d.members as Record<string, string>[])[0]?.furigana : '')
    ),
    applicantBirthDate: d.applicantBirthDate ?? '',
    applicantPhone: phoneDigitsOnly(d.applicantPhone),
    applicantEmail: d.applicantEmail ?? '',
    currentAddress: d.currentAddress ?? '',
    applicationDate: d.applicationDate ?? '',
    caseStatus: d.caseStatus ?? '',
    householdNo: d.householdNo ?? '',
    members: Array.isArray(d.members) && d.members.length
      ? (d.members as Record<string, string>[]).map((m) => {
          const { benefitPeriodStart, benefitPeriodEnd } = splitBenefitPeriod(m.benefitPeriod ?? '');
          return {
            ...emptyMember(),
            ...m,
            furigana: normalizeJapaneseName(m.furigana),
            name: normalizeJapaneseName(m.name),
            phone: phoneDigitsOnly(m.phone),
            // 検討事項 No.66: 申出者は世帯構成1行目とは限らないため、1行目への「世帯主本人」固定を廃止する。
            // 旧データの世帯主トークン「本人」は「世帯主本人」へ正規化し、
            // セレクト選択肢（APP_DOC_RELATION_OPTIONS）内に収める。
            relationToHead: m.relationToHead === '本人' ? '世帯主本人' : (m.relationToHead ?? ''),
            benefitPeriodStart,
            benefitPeriodEnd,
          } as FormMember;
        })
      : [emptyMember()],
    protectionResidenceSame: residenceMode,
    pastAddresses:
      residenceMode === 'same'
        ? []
        : Array.isArray(d.pastAddresses) && d.pastAddresses.length
          ? (d.pastAddresses as Record<string, string>[]).map((p) => {
              const periodMonths = normalizePastAddressPeriodMonths(p.periodStartMonth, p.periodEndMonth, p.period);
              return {
                address: p.address ?? '',
                period: p.period ?? '',
                periodStartMonth: periodMonths.periodStartMonth,
                periodEndMonth: periodMonths.periodEndMonth,
              };
            })
          : [],
    bank: {
      accountHolderKana: d.bank?.accountHolderKana ?? '',
      bankCode: d.bank?.bankCode ?? '',
      bankBranchCode: d.bank?.bankBranchCode ?? '',
      accountType: normalizeApplicationBankAccountType(d.bank?.accountType),
      accountNumber: d.bank?.accountNumber ?? '',
    },
    otherHouseholdMembers: Array.isArray(d.otherHouseholdMembers)
      ? (d.otherHouseholdMembers as Record<string, string>[]).map((o) => ({ name: normalizeJapaneseName(o.name), phone: phoneDigitsOnly(o.phone), email: o.email ?? '' }))
      : [],
    additionRows: Array.isArray(d.additionRows)
      ? (d.additionRows as Record<string, string>[]).map((a) => ({
          category: normalizeAddonCategory(a.category) || APP_DOC_ADDON_TYPES[0],
          amount: a.amount ?? '',
          name: a.name ?? '',
          periodStartMonth: a.periodStartMonth ?? '',
          periodEndMonth: a.periodEndMonth ?? '',
          facility: a.facility ?? '',
          evidenceFileName: a.evidenceFileName ?? '',
          evidenceKey: a.evidenceKey ?? '',
        }))
      : [],
    attachments: normalizeAttachmentsForForm(d.attachments),
    memo: d.memo ?? '',
  };
};

const createEmptyCreateForm = (identId: string) => ({
  ...mergeForm(null),
  identId,
});

const emptyPastAddress = (): FormPastAddress => ({
  address: '',
  period: '',
  periodStartMonth: '',
  periodEndMonth: '',
});

const membersToPayload = (members: FormMember[]): Record<string, string>[] =>
  members.map((m) => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(m)) {
      if (typeof v === 'string') out[k] = v;
    }
    out.phone = phoneDigitsOnly(m.phone);
    out.furigana = normalizeJapaneseName(m.furigana);
    out.name = normalizeJapaneseName(m.name);
    out.benefitPeriod = m.benefitPeriodStart || m.benefitPeriodEnd
      ? `${m.benefitPeriodStart ?? ''}～${m.benefitPeriodEnd ?? ''}`
      : '';
    return out;
  });

const pastAddressesToPayload = (pastAddresses: FormPastAddress[]): Record<string, string>[] =>
  pastAddresses.map((p) => ({
    address: p.address ?? '',
    periodStartMonth: p.periodStartMonth ?? '',
    periodEndMonth: p.periodEndMonth ?? '',
  }));

const otherHouseholdMembersToPayload = (members: FormOtherMember[]): Record<string, string>[] =>
  members.map((member) => ({
    name: normalizeJapaneseName(member.name),
    phone: phoneDigitsOnly(member.phone),
    email: member.email ?? '',
  }));

const additionRowsToPayload = (rows: FormAdditionRow[]): Record<string, string>[] =>
  rows.map((row) => ({
    category: row.category ?? '',
    amount: row.amount ?? '',
    name: row.name ?? '',
    periodStartMonth: row.periodStartMonth ?? '',
    periodEndMonth: row.periodEndMonth ?? '',
    facility: row.facility ?? '',
    // 行と証憑の安定対応付けキー（行削除/並べ替えでも証憑がズレないよう index 依存をやめる）。
    evidenceKey: row.evidenceKey ?? '',
  }));

// 加算証憑の安定キー（数字のみ＝バックエンドの addonEvidence_<数字> 検証を満たす）。
let addonEvidenceSeq = 0;
const makeAddonEvidenceKey = () => {
  addonEvidenceSeq = (addonEvidenceSeq + 1) % 100000;
  return `addonEvidence_${Date.now()}${addonEvidenceSeq}`;
};

const applicationInfoSnapshot = (form: FormState): string =>
  JSON.stringify({
    applicationDate: String(form.applicationDate || '').trim(),
    currentAddress: String(form.currentAddress || '').trim(),
    householdNo: String(form.householdNo || '').trim(),
    applicantName: normalizeJapaneseName(form.name),
    applicantFurigana: normalizeJapaneseName(form.applicantFurigana),
    applicantBirthDate: String(form.applicantBirthDate || '').trim(),
    applicantPhone: phoneDigitsOnly(form.applicantPhone),
    applicantEmail: String(form.applicantEmail || '').trim(),
    protectionResidenceSame: form.protectionResidenceSame,
    members: membersToPayload(form.members),
    pastAddresses: form.protectionResidenceSame === 'same' ? [] : pastAddressesToPayload(form.pastAddresses),
    bank: {
      accountHolderKana: form.bank.accountHolderKana ?? '',
      bankCode: form.bank.bankCode ?? '',
      bankBranchCode: form.bank.bankBranchCode ?? '',
      accountType: form.bank.accountType ?? '',
      accountNumber: form.bank.accountNumber ?? '',
    },
    otherHouseholdMembers: otherHouseholdMembersToPayload(form.otherHouseholdMembers),
    additionRows: additionRowsToPayload(form.additionRows),
  });

const scrollToFirstValidationError = () => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const firstError = document.querySelector<HTMLElement>('.customer-inline-error');
      if (!firstError) return;

      const target = (firstError.closest('.ant-form-item') as HTMLElement | null) ?? firstError;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const focusTarget = target.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), .ant-select-selector, button:not([disabled])'
      );
      focusTarget?.focus({ preventScroll: true });
    });
  });
};

/* ========================================================
   申出書編集画面
======================================================== */
const ApplicationDocEdit = ({ data, setData, returnPage }: ApplicationDocEditProps) => {
  const navigate = useAppNavigate();
  const toast = useToast();
  const { role, hasPermission } = usePermission();
  const { applicationId, identId: routeIdentId } = useParams<{ applicationId?: string; identId?: string }>();
  const routeApplicationId = parsePositiveInt(applicationId);
  const queryIdentId = routeIdentId?.trim() ?? '';
  const isCreateMode = !routeApplicationId && !!queryIdentId;

  const [form, setForm] = useState<FormState>(() => {
    if (isCreateMode) {
      return createEmptyCreateForm(queryIdentId);
    }
    const base = mergeForm(data);
    return queryIdentId ? { ...base, identId: queryIdentId } : base;
  });
  const [initialApplicationInfoSnapshot, setInitialApplicationInfoSnapshot] = useState(() => applicationInfoSnapshot(form));
  const [commentRecipientId, setCommentRecipientId] = useState<number | null>(null);
  const [loading, setLoading] = useState(Boolean(routeApplicationId));
  const [saving, setSaving] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<AppDocCoreSubmitErrors>({});
  const [attachmentFiles, setAttachmentFiles] = useState<Record<string, File[]>>({});
  const [attachmentDeletes, setAttachmentDeletes] = useState<Set<string>>(() => new Set());
  const [attachmentFileDeletes, setAttachmentFileDeletes] = useState<Record<string, Set<number>>>({});

  // commentOnly: (1) 非コメント編集権限なし、または (2) 現在ステータスが 5 完了系で
  // backend `CaseStatus.canEdit` が false になるケースのいずれかで true。
  // 申出書詳細では welfare-track 別の制御はないため isWelfareEnded は false 固定で十分。
  // statusBlocksNonCommentEdit は非コメント編集権限を持つロール限定。コールセンター（一般）の
  // ように元から非コメント編集権限がないロールは、ステータスに関係なくコメント編集可能。
  const docStatusKey = caseStatusKeyByLabel(form.caseStatus);
  const statusBlocksNonCommentEdit = !isCreateMode
    && hasPermission('APPLICATION_EDIT_NON_COMMENT')
    && docStatusKey != null
    && !caseStatusPermissions(docStatusKey, false, role).canEdit;
  const commentOnly = !isCreateMode
    && (!hasPermission('APPLICATION_EDIT_NON_COMMENT') || statusBlocksNonCommentEdit);

  useEffect(() => {
    // ルートIDの有無でフォームを初期化/取得する正当なデータ取得 effect。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!routeApplicationId) {
      const base = isCreateMode ? createEmptyCreateForm(queryIdentId) : mergeForm(data);
      const nextForm = queryIdentId ? { ...base, identId: queryIdentId } : base;
      setForm(nextForm);
      setInitialApplicationInfoSnapshot(applicationInfoSnapshot(nextForm));
      setCommentRecipientId(null);
      setSubmitErrors({});
      setAttachmentFiles({});
      setAttachmentDeletes(new Set());
      setAttachmentFileDeletes({});
      setLoading(false);
      return;
    }
    /* eslint-enable react-hooks/set-state-in-effect */

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await applicationDocsApi.getById(routeApplicationId);
        if (!cancelled) {
          const nextForm = mergeForm(response.data);
          setForm(nextForm);
          setInitialApplicationInfoSnapshot(applicationInfoSnapshot(nextForm));
          setCommentRecipientId(null);
          setData(response.data);
          setSubmitErrors({});
          setAttachmentFiles({});
          setAttachmentDeletes(new Set());
          setAttachmentFileDeletes({});
        }
      } catch (error: unknown) {
        if (isHandledAuthError(error)) return;
        if (!cancelled) {
          navigate('recipient-search');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeApplicationId, isCreateMode, queryIdentId, navigate, setData, toast]);

  useEffect(() => {
    if (!isCreateMode || !queryIdentId) return;
    let cancelled = false;
    const fetchRecipient = async () => {
      try {
        const response = await recipientsApi.list({ identId: queryIdentId, page: 0, size: 1 });
        if (cancelled) return;
        const recipient = response.data.content[0];
        if (!recipient) return;
        setCommentRecipientId(recipient.id);
        // コメントは recipients.comment を唯一の保存先として共通化（backend 2026-05-19）。
        // 新規登録画面でも受給者の現コメントを初期表示し、編集すれば create 時に recipient.comment へ反映される。
        setForm((prev) => {
          const nextForm = {
            ...prev,
            householdNo: recipient.householdIdentCode || prev.householdNo,
            memo: recipient.comment ?? prev.memo,
          };
          setInitialApplicationInfoSnapshot(applicationInfoSnapshot(nextForm));
          return nextForm;
        });
      } catch (error) {
        if (isHandledAuthError(error)) return;
      }
    };
    void fetchRecipient();
    return () => {
      cancelled = true;
    };
  }, [isCreateMode, queryIdentId]);

  const updateField = (key: keyof FormState, val: string) => setForm((prev) => ({ ...prev, [key]: val }));
  const updateBank = (key: string, val: string, skipSanitize = false) =>
    setForm((prev) => ({
      ...prev,
      bank: {
        ...prev.bank,
        [key]: skipSanitize ? val : sanitizeBankFieldInput(key, val),
      },
    }));
  const updateMember = (idx: number, key: string, val: string) =>
    setForm((prev) => ({
      ...prev,
      members: prev.members.map((m, i) => (i === idx ? { ...m, [key]: key === 'phone' ? phoneDigitsOnly(val) : val } : m)),
    }));
  // 続柄セレクト変更。「その他」以外を選んだら relationToHeadOther（自由入力）をクリアする。
  const updateMemberRelation = (idx: number, val: string) =>
    setForm((prev) => ({
      ...prev,
      members: prev.members.map((m, i) =>
        i === idx ? { ...m, relationToHead: val, relationToHeadOther: val === 'その他' ? m.relationToHeadOther ?? '' : '' } : m,
      ),
    }));
  // 申出者情報欄の入力値を、指定した世帯員の行へ転記する（検討事項 No.66）。
  // 申出者は世帯主とは限らないため、どの行へ転記するかは利用者が選ぶ。
  const applyApplicantToMember = (idx: number) =>
    setForm((prev) => ({
      ...prev,
      members: prev.members.map((m, i) =>
        i === idx
          ? {
              ...m,
              // 申出者情報が未入力の項目は、その行の既存入力を消さずに残す。
              furigana: normalizeJapaneseName(prev.applicantFurigana || m.furigana || ''),
              name: normalizeJapaneseName(prev.name || m.name || ''),
              birthDate: prev.applicantBirthDate || m.birthDate || '',
            }
          : m,
      ),
    }));
  const addMember = () => setForm((prev) => prev.members.length >= 8 ? prev : { ...prev, members: [...prev.members, emptyMember()] });
  const removeMember = (idx: number) =>
    setForm((prev) =>
      prev.members.length <= 1
        ? prev
        // 検討事項 No.66: 1行目への「世帯主本人」固定を廃止したため、削除時の続柄書き換えも行わない。
        : { ...prev, members: prev.members.filter((_, i) => i !== idx) },
    );
  const updatePast = (idx: number, key: keyof FormPastAddress, val: string) =>
    setForm((prev) => ({
      ...prev,
      pastAddresses: prev.pastAddresses.map((p, i) => (i === idx ? { ...p, [key]: val } : p)),
    }));
  const addPastAddress = () =>
    setForm((prev) => prev.pastAddresses.length >= 3 ? prev : { ...prev, pastAddresses: [...prev.pastAddresses, emptyPastAddress()] });
  const removePastAddress = (idx: number) =>
    setForm((prev) => ({ ...prev, pastAddresses: prev.pastAddresses.filter((_, i) => i !== idx) }));
  const updateOther = (idx: number, key: keyof FormOtherMember, val: string) =>
    setForm((prev) => ({
      ...prev,
      otherHouseholdMembers: prev.otherHouseholdMembers.map((o, i) => (
        i === idx ? { ...o, [key]: key === 'phone' ? phoneDigitsOnly(val) : val } : o
      )),
    }));
  const addOtherMember = () =>
    setForm((prev) => {
      if (prev.otherHouseholdMembers.length >= 2) return prev;
      return { ...prev, otherHouseholdMembers: [...prev.otherHouseholdMembers, { name: '', phone: '', email: '' }] };
    });
  const removeOtherMember = (idx: number) =>
    setForm((prev) => ({ ...prev, otherHouseholdMembers: prev.otherHouseholdMembers.filter((_, i) => i !== idx) }));
  const updateAddition = (idx: number, key: keyof FormAdditionRow, val: string) =>
    setForm((prev) => ({ ...prev, additionRows: prev.additionRows.map((a, i) => (i === idx ? { ...a, [key]: val } : a)) }));
  const addAdditionRow = () =>
    setForm((prev) => ({ ...prev, additionRows: [...prev.additionRows, emptyAddition()] }));
  // 加算証憑は行ごとの安定キー（evidenceKey）で添付する。未採番なら採番してから取り込む。
  const selectAddonEvidence = (idx: number, fileList: FileList | null | undefined) => {
    const existing = String(form.additionRows[idx]?.evidenceKey ?? '').trim();
    const key = existing || makeAddonEvidenceKey();
    if (!existing) updateAddition(idx, 'evidenceKey', key);
    selectAttachmentFiles(key, fileList);
  };
  const removeAdditionRow = (idx: number) => {
    // 行削除時はその行の証憑（安定キー）を削除対象にし、孤児ファイルを残さない。
    // 安定キーのため、他行の証憑が index ズレで誤対応することはない。
    const key = String(form.additionRows[idx]?.evidenceKey ?? '').trim();
    if (key) {
      clearSelectedAttachmentFiles(key);
      if (currentAttachmentFileCount(key) > 0) markAttachmentDelete(key);
    }
    setForm((prev) => ({ ...prev, additionRows: prev.additionRows.filter((_, i) => i !== idx) }));
  };

  const effectiveApplicationId = routeApplicationId;
  const goBack = () => {
    if (effectiveApplicationId) {
      navigate('application-document-detail', { applicationId: effectiveApplicationId });
    } else if (isCreateMode) {
      navigate('recipient-search');
    } else {
      navigate(returnPage === 'recipient-search' ? 'recipient-search' : 'application-document-detail');
    }
  };

  const markAttachmentDelete = (key: string) => {
    setAttachmentFiles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setAttachmentDeletes((prev) => new Set(prev).add(key));
  };

  const undoAttachmentDelete = (key: string) => {
    setAttachmentDeletes((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // 保存済み複数ファイルのうち 1 件だけを削除対象に登録/解除する（index ベース）。
  const markSavedFileDelete = (key: string, idx: number) => {
    setAttachmentFileDeletes((prev) => {
      const nextSet = new Set(prev[key] ?? []);
      nextSet.add(idx);
      return { ...prev, [key]: nextSet };
    });
  };

  const undoSavedFileDelete = (key: string, idx: number) => {
    setAttachmentFileDeletes((prev) => {
      const current = prev[key];
      if (!current || !current.has(idx)) return prev;
      const nextSet = new Set(current);
      nextSet.delete(idx);
      const next = { ...prev };
      if (nextSet.size === 0) delete next[key];
      else next[key] = nextSet;
      return next;
    });
  };

  // 選択済み（未アップロード）のファイルから 1 件だけを取り除く。
  const removeSelectedAttachmentFileAt = (key: string, idx: number) => {
    setAttachmentFiles((prev) => {
      const current = prev[key];
      if (!current) return prev;
      const nextFiles = current.filter((_, i) => i !== idx);
      const next = { ...prev };
      if (nextFiles.length === 0) delete next[key];
      else next[key] = nextFiles;
      return next;
    });
  };

  const currentAttachmentFileCount = (key: string) => {
    if (attachmentDeletes.has(key)) return 0;
    const attachment = form.attachments?.[key] as Parameters<typeof attachmentFilesOf>[0];
    const total = attachmentFilesOf(attachment).length;
    const deleted = attachmentFileDeletes[key]?.size ?? 0;
    return Math.max(0, total - deleted);
  };

  const selectedAttachmentFileCount = (key: string) => attachmentFiles[key]?.length ?? 0;

  const effectiveAttachmentFileCount = (key: string) => {
    const selectedCount = selectedAttachmentFileCount(key);
    if (selectedCount > 0 && !isMultiFileAttachmentKey(key)) return selectedCount;
    return currentAttachmentFileCount(key) + selectedCount;
  };

  const hasEffectiveAttachment = (key: string) => effectiveAttachmentFileCount(key) > 0;

  const missingRequiredAttachmentKeys = () => {
    const missing = new Set<string>();
    const hasAnyId = REQUIRED_ID_ATTACHMENT_KEYS.some((key) => hasEffectiveAttachment(key));
    if (!hasAnyId) {
      REQUIRED_ID_ATTACHMENT_KEYS.forEach((key) => missing.add(key));
    }
    if (!hasEffectiveAttachment('kosekiAll')) missing.add('kosekiAll');
    if (!hasEffectiveAttachment('bankBookOrCard')) missing.add('bankBookOrCard');
    return Array.from(missing);
  };

  const selectAttachmentFiles = (key: string, files: FileList | null | undefined) => {
    const incomingFiles = Array.from(files || []);
    const multiFile = isMultiFileAttachmentKey(key);
    const maxFiles = multiFile ? MAX_FILES_PER_ATTACHMENT : 1;
    const existingCount = multiFile ? currentAttachmentFileCount(key) + selectedAttachmentFileCount(key) : 0;
    const remainingSlots = Math.max(0, maxFiles - existingCount);
    if (incomingFiles.length === 0) return;
    if (remainingSlots <= 0) {
      Modal.error({ content: `ファイルは最大${maxFiles}件までです。` });
      return;
    }
    const validFiles: File[] = [];
    let rejectedByValidation = 0;
    for (const file of incomingFiles) {
      if (validFiles.length >= remainingSlots) break;
      const error = validateAttachmentFile(file);
      if (error) {
        Modal.error({ content: error });
        rejectedByValidation += 1;
        continue;
      }
      validFiles.push(file);
    }
    if (multiFile && incomingFiles.length - rejectedByValidation > remainingSlots) {
      Modal.warning({ content: `ファイルは最大${maxFiles}件までです。${remainingSlots}件に制限して取り込みました。` });
    }
    if (validFiles.length === 0) return;
    setAttachmentFiles((prev) => ({
      ...prev,
      [key]: isMultiFileAttachmentKey(key) ? [...(prev[key] || []), ...validFiles] : validFiles,
    }));
    undoAttachmentDelete(key);
    // 同じ枠を選び直したら、その枠の index 単位削除予約も取り消しておく。
    const pendingIndexDeletes = attachmentFileDeletes[key];
    if (pendingIndexDeletes) {
      for (const idx of pendingIndexDeletes) undoSavedFileDelete(key, idx);
    }
  };

  const clearSelectedAttachmentFiles = (key: string) => {
    setAttachmentFiles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // yyyy-MM-dd 同士の大小比較。どちらかが未入力/不正な形式のときは判定しない（必須チェック側に任せる）。
  const isDateOrderReversed = (earlier?: string, later?: string): boolean => {
    const a = String(earlier ?? '').trim();
    const b = String(later ?? '').trim();
    const pattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!pattern.test(a) || !pattern.test(b)) return false;
    return b > a;
  };

  const validate = (): AppDocCoreSubmitErrors => {
    const errors: AppDocCoreSubmitErrors = {};

    if (!String(form.householdNo ?? '').trim()) {
      errors.householdNo = '世帯番号を入力してください。';
    } else if (String(form.householdNo).trim().length > 255) {
      // DBカラム長超過は backend で 409「データ競合」になり原因が分からないため、フロントで桁数を弾く。
      errors.householdNo = '世帯番号は255文字以内で入力してください。';
    }
    // 申出者情報（検討事項 No.66）: 世帯構成とは独立した項目になったため、ここで必須検証する。
    if (!String(form.name ?? '').trim()) {
      errors.applicantName = '代表者氏名を入力してください。';
    }
    const applicantFuriganaError = furiganaSplitErrorMessage(form.applicantFurigana);
    if (applicantFuriganaError) {
      errors.applicantFurigana = applicantFuriganaError;
    } else if (!String(form.applicantFurigana ?? '').trim()) {
      errors.applicantFurigana = 'フリガナを入力してください。';
    }
    if (!String(form.applicantBirthDate ?? '').trim()) {
      errors.applicantBirthDate = '生年月日を入力してください。';
    } else if (isDateOrderReversed(form.applicationDate, form.applicantBirthDate)) {
      // 生年月日 <= 申出年月日（指摘 2026-08）。エラーは順序を直す側である生年月日欄に出す。
      errors.applicantBirthDate = '生年月日は申出年月日以前の日付を入力してください。';
    }
    const applicantPhone = phoneDigitsOnly(form.applicantPhone);
    if (!applicantPhone) {
      errors.applicantPhone = '電話番号を入力してください。';
    } else if (!isValidPhoneNumber(applicantPhone)) {
      errors.applicantPhone = '電話番号の形式が正しくありません。';
    }
    // メールアドレスはプロトタイプ version2 に倣い任意。入力がある場合のみ形式チェック。
    const emailValue = String(form.applicantEmail ?? '').trim();
    if (emailValue && !EMAIL_PATTERN.test(emailValue)) {
      errors.applicantEmail = 'メールアドレスの形式が正しくありません。';
    }
    if (!String(form.applicationDate ?? '').trim()) {
      errors.applicationDate = '申出年月日を入力してください。';
    }
    if (!String(form.currentAddress ?? '').trim()) {
      errors.currentAddress = '現住所を入力してください。';
    } else if (String(form.currentAddress).trim().length > 512) {
      errors.currentAddress = '現住所は512文字以内で入力してください。';
    }

    const requiredMemberFields: [string, string][] = [
      ['name', '氏名を入力してください。'],
      ['relationToHead', '世帯主との続柄を入力してください。'],
      ['birthDate', '生年月日を入力してください。'],
      ['benefitPeriodStart', '生活保護受給期間（開始）を入力してください。'],
      ['benefitPeriodEnd', '生活保護受給期間（終了）を入力してください。'],
    ];
    const memberErrors: Record<number, MemberErrors> = {};
    form.members.forEach((m, mi) => {
      const mErr: MemberErrors = {};
      for (const [field, msg] of requiredMemberFields) {
        if (!String(m[field] ?? '').trim()) mErr[field] = msg;
      }
      if (String(m.relationToHead ?? '').trim() === 'その他' && !String(m.relationToHeadOther ?? '').trim()) {
        mErr.relationToHeadOther = '続柄（その他）の内容を入力してください。';
      }
      const furiganaError = furiganaSplitErrorMessage(m.furigana);
      if (furiganaError) mErr.furigana = furiganaError;
      // DBカラム長(name/furigana=128, relation_to_head_other=64)超過は backend で 409 になるため桁数を弾く。
      if (!mErr.name && String(m.name ?? '').trim().length > 128) {
        mErr.name = '氏名は128文字以内で入力してください。';
      }
      if (!mErr.furigana && String(m.furigana ?? '').trim().length > 128) {
        mErr.furigana = 'フリガナは128文字以内で入力してください。';
      }
      if (!mErr.relationToHeadOther
          && String(m.relationToHead ?? '').trim() === 'その他'
          && String(m.relationToHeadOther ?? '').trim().length > 64) {
        mErr.relationToHeadOther = '続柄（その他）は64文字以内で入力してください。';
      }
      // 世帯員のメール・電話はプロトタイプ version2 に存在しないため検証しない。
      const benefitStart = parseComparableDate(m.benefitPeriodStart);
      const benefitEnd = parseComparableDate(m.benefitPeriodEnd);
      if (
        benefitStart !== null &&
        benefitEnd !== null &&
        benefitStart > benefitEnd &&
        !mErr.benefitPeriodEnd
      ) {
        mErr.benefitPeriodEnd = '生活保護受給期間（終了）は生活保護受給期間（開始）以降の日付を入力してください。';
      }
      // 生年月日 <= 生活保護受給期間開始 <= 終了（JTB 指摘 2026-05、客先修正後）:
      //   出生前の日付を受給期間に選べないよう、生年月日は受給開始日以前であること。
      //   （開始 <= 終了 は既存チェックで担保。birth <= 開始 が成り立てば birth <= 終了 も自動成立）
      const birth = parseComparableDate(m.birthDate);
      if (
        birth !== null &&
        benefitStart !== null &&
        birth > benefitStart &&
        !mErr.birthDate
      ) {
        mErr.birthDate = '生年月日は生活保護受給期間（開始）以前の日付を入力してください。';
      }
      // 検討事項 No.66: 「1行目は死亡にできない」制限は撤廃した。
      // 従来は 1行目＝世帯主本人＝申出者（＝決定通知書の宛名）だったため死亡者を置けなかったが、
      // 申出者を申出者情報欄で独立して入力するようになり、当時の世帯主が死亡している世帯では
      // 死亡した世帯主を世帯構成に記載するのが正しい状態になったため。
      if (Object.keys(mErr).length > 0) memberErrors[mi] = mErr;
    });
    if (Object.keys(memberErrors).length > 0) errors.members = memberErrors;

    const bankErrors: Record<string, string> = {};
    const bankCodeErr = bankCodeError(form.bank.bankCode);
    if (bankCodeErr) bankErrors.bankCode = bankCodeErr;
    const branchCodeErr = bankBranchCodeError(form.bank.bankBranchCode);
    if (branchCodeErr) bankErrors.bankBranchCode = branchCodeErr;
    if (!String(form.bank.accountType ?? '').trim()) {
      bankErrors.accountType = '預金種目を選択してください。';
    }
    const accountNumberErr = bankAccountNumberError(form.bank.accountNumber);
    if (accountNumberErr) bankErrors.accountNumber = accountNumberErr;
    const accountHolderErr = bankAccountHolderKanaError(form.bank.accountHolderKana);
    if (accountHolderErr) bankErrors.accountHolderKana = accountHolderErr;
    else if (String(form.bank.accountHolderKana ?? '').trim().length > 128) {
      bankErrors.accountHolderKana = '口座名義（カナ）は128文字以内で入力してください。';
    }
    if (Object.keys(bankErrors).length > 0) errors.bank = bankErrors;

    const otherErrors: Record<number, Record<string, string>> = {};
    form.otherHouseholdMembers.forEach((o, oi) => {
      const rowErrors: Record<string, string> = {};
      const phone = phoneDigitsOnly(o.phone);
      if (phone && !isValidPhoneNumber(phone)) {
        rowErrors.phone = '電話番号の形式が正しくありません。';
      }
      // 連絡先のメールはプロトタイプ version2 に存在しないため検証しない。
      if (Object.keys(rowErrors).length > 0) otherErrors[oi] = rowErrors;
    });
    if (Object.keys(otherErrors).length > 0) errors.otherHouseholdMembers = otherErrors;

    const missingAttachments = missingRequiredAttachmentKeys();
    if (missingAttachments.length > 0) {
      errors.attachments = '必須提出書類に対応する証明書（ファイル）の添付が不足しています。';
      errors.missingAttachmentKeys = missingAttachments;
    }

    return errors;
  };

  const saveAndBack = async () => {
    const hasMemoInput = String(form.memo ?? '').trim() !== '';
    const hasAttachmentChanges = attachmentDeletes.size > 0
      || Object.values(attachmentFileDeletes).some((idxSet) => idxSet.size > 0)
      || Object.values(attachmentFiles).some((files) => files.length > 0);
    const hasApplicationInfoChanges = applicationInfoSnapshot(form) !== initialApplicationInfoSnapshot || hasAttachmentChanges;
    const memoOnlySave = hasMemoInput && !hasApplicationInfoChanges && (!isCreateMode || commentRecipientId != null);
    const nextErrors = memoOnlySave ? {} : validate();
    setSubmitErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      scrollToFirstValidationError();
      return;
    }

    if (memoOnlySave) {
      setSaving(true);
      try {
        if (effectiveApplicationId) {
          const response = await applicationDocsApi.update(effectiveApplicationId, { memo: form.memo ?? '' });
          setData(response.data);
          toast.success('保存しました。');
          navigate('application-document-detail', { applicationId: effectiveApplicationId });
          return;
        }
        if (isCreateMode && commentRecipientId != null) {
          await recipientsApi.update(commentRecipientId, { comment: form.memo ?? '' });
          setData({ ...form, memo: form.memo ?? '' });
          toast.success('保存しました。');
          navigate('recipient-search');
          return;
        }
        setData({ ...form, memo: form.memo ?? '' });
        toast.success('保存しました。');
        navigate(returnPage === 'recipient-search' ? 'recipient-search' : 'application-document-detail');
        return;
      } catch (error) {
        if (isHandledAuthError(error)) return;
      } finally {
        setSaving(false);
      }
      return;
    }

    // 申出者名は申出者情報欄の「代表者氏名（表示用）」を採用する（検討事項 No.66）。
    // 申出者は世帯構成1行目とは限らないため、世帯構成からは導出しない。
    const applicantName = normalizeJapaneseName(form.name);
    const applicantFurigana = normalizeJapaneseName(form.applicantFurigana);

    if (!effectiveApplicationId) {
      if (isCreateMode) {
        const createPayload: ApplicationDocCreateRequest = {
          identId: queryIdentId,
          applicantName,
          applicantFurigana,
          applicantBirthDate: String(form.applicantBirthDate || '').trim() || undefined,
          applicantPhone: phoneDigitsOnly(form.applicantPhone),
          applicantEmail: String(form.applicantEmail || '').trim(),
          currentAddress: String(form.currentAddress || '').trim(),
          applicationDate: String(form.applicationDate || '').trim() || undefined,
          householdNo: String(form.householdNo || '').trim(),
          memo: form.memo ?? '',
          protectionResidenceSame: form.protectionResidenceSame === 'same',
          members: membersToPayload(form.members),
          pastAddresses: form.protectionResidenceSame === 'same' ? [] : pastAddressesToPayload(form.pastAddresses),
          bank: form.bank,
          otherHouseholdMembers: otherHouseholdMembersToPayload(form.otherHouseholdMembers),
          additionRows: additionRowsToPayload(form.additionRows),
        };
        setSaving(true);
        let created: ApplicationDocResponse | null = null;
        try {
          try {
            const response = await applicationDocsApi.create(createPayload);
            created = response.data;
          } catch (error) {
            if (isHandledAuthError(error)) return;
            return;
          }
          if (!created) return;

          let uploadFailed = false;
          for (const [key, files] of Object.entries(attachmentFiles)) {
            if (!files.length) continue;
            try {
              for (const file of files) {
                const uploaded = await applicationDocsApi.uploadAttachment(created.id, key, file, { _silent: true });
                created = uploaded.data;
              }
            } catch (uploadError) {
              if (isHandledAuthError(uploadError)) return;
              uploadFailed = true;
              break;
            }
          }

          if (uploadFailed) {
            setData(created);
            Modal.error({ content: '登録しましたが、添付ファイルのアップロードに失敗しました。詳細画面から再度お試しください。' });
            navigate('application-document-detail', { applicationId: created.id });
            return;
          }

          try {
            const submitted = await applicationDocsApi.submit(created.id);
            created = submitted.data;
            setData(created);
            toast.success('登録しました。');
          } catch (submitError) {
            if (isHandledAuthError(submitError)) return;
            setData(created);
          }
          navigate('application-document-detail', { applicationId: created.id });
        } finally {
          setSaving(false);
        }
        return;
      }

      const nextAttachments = { ...(form.attachments || {}) };
      attachmentDeletes.forEach((key) => {
        nextAttachments[key] = null;
      });
      // 保存済みファイルの index 単位削除を楽観表示にも反映する（whole-key 削除済みキーは除く）。
      Object.entries(attachmentFileDeletes).forEach(([key, idxSet]) => {
        if (attachmentDeletes.has(key)) return;
        const remaining = attachmentFilesOf(form.attachments?.[key] as Parameters<typeof attachmentFilesOf>[0])
          .filter((_, i) => !idxSet.has(i));
        if (remaining.length === 0) {
          nextAttachments[key] = null;
        } else {
          nextAttachments[key] = { ...remaining[0], files: remaining };
        }
      });
      Object.entries(attachmentFiles).forEach(([key, files]) => {
        if (files.length > 0) {
          const file = files[0];
          nextAttachments[key] = {
            fileName: file.name,
            uploadedAt: new Date().toISOString(),
            contentType: file.type,
            size: file.size,
            files: files.map((item) => ({
              fileName: item.name,
              uploadedAt: new Date().toISOString(),
              contentType: item.type,
              size: item.size,
            })),
          };
        }
      });
      const next = {
        ...form,
        applicantName,
        applicantFurigana,
        applicantBirthDate: String(form.applicantBirthDate || '').trim(),
        name: applicantName,
        applicantPhone: phoneDigitsOnly(form.applicantPhone),
        members: membersToPayload(form.members),
        protectionResidenceSame: form.protectionResidenceSame === 'same',
        pastAddresses: form.protectionResidenceSame === 'same' ? [] : pastAddressesToPayload(form.pastAddresses),
        otherHouseholdMembers: otherHouseholdMembersToPayload(form.otherHouseholdMembers),
        attachments: nextAttachments,
      };
      setData(next);
      toast.success('保存しました。');
      navigate('application-document-detail');
      return;
    }

    // コメントのみ編集可能なロール（CALL_CENTER_SV / CALL_CENTER_AGENT）は memo のみ送信し、
    // 後端の requestChangesNonMemoFields ガードに引っ掛からないようにする。
    const payload: ApplicationDocUpdateRequest = commentOnly
      ? { memo: form.memo ?? '' }
      : {
          applicantName,
          applicantFurigana,
          applicantBirthDate: String(form.applicantBirthDate || '').trim() || undefined,
          applicantPhone: phoneDigitsOnly(form.applicantPhone),
          applicantEmail: String(form.applicantEmail || '').trim(),
          currentAddress: String(form.currentAddress || '').trim(),
          applicationDate: String(form.applicationDate || '').trim() || undefined,
          householdNo: String(form.householdNo || '').trim(),
          memo: form.memo ?? '',
          protectionResidenceSame: form.protectionResidenceSame === 'same',
          members: membersToPayload(form.members),
          pastAddresses: form.protectionResidenceSame === 'same' ? [] : pastAddressesToPayload(form.pastAddresses),
          bank: form.bank,
          otherHouseholdMembers: otherHouseholdMembersToPayload(form.otherHouseholdMembers),
          additionRows: additionRowsToPayload(form.additionRows),
        };

    setSaving(true);
    try {
      await applicationDocsApi.update(effectiveApplicationId, payload);
      if (!commentOnly) {
        for (const key of attachmentDeletes) {
          await applicationDocsApi.deleteAttachment(effectiveApplicationId, key);
        }
        // whole-key 削除されていないキーについて、保存済みファイルを index 単位で削除する。
        // index ずれを避けるため降順で削除する。
        for (const [key, idxSet] of Object.entries(attachmentFileDeletes)) {
          if (attachmentDeletes.has(key)) continue;
          for (const idx of Array.from(idxSet).sort((a, b) => b - a)) {
            await applicationDocsApi.deleteAttachment(effectiveApplicationId, key, idx);
          }
        }
        for (const [key, files] of Object.entries(attachmentFiles)) {
          for (const file of files) {
            await applicationDocsApi.uploadAttachment(effectiveApplicationId, key, file);
          }
        }
      }
      const fresh = await applicationDocsApi.getById(effectiveApplicationId);
      // 編集保存は常に「再提出」として扱い、案件ステータスを 申出書確認中 に戻す。
      // submit 側で受け付け不能な状態（通知書出力以降）の場合のみ fresh のまま置く。
      // コメントのみ編集の場合は再提出をスキップ（権限不足 / 案件ステータス制限のため）。
      let submitFailed = false;
      if (!commentOnly) {
        try {
          const resubmitted = await applicationDocsApi.submit(effectiveApplicationId);
          setData(resubmitted.data);
        } catch (submitError) {
          if (isHandledAuthError(submitError)) return;
          submitFailed = true;
          setData(fresh.data);
        }
      } else {
        setData(fresh.data);
      }
      if (!submitFailed) {
        toast.success('保存しました。');
      }
      navigate('application-document-detail', { applicationId: effectiveApplicationId });
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setSaving(false);
    }
  };

  // [フィールドキー, ラベル, inputタイプ, 必須フラグ]
  const memberFields: [string, string, string, boolean][] = [
    // プロトタイプ version2 に合わせた世帯員フィールド（メール・電話は持たない）。
    ['furigana', 'フリガナ', 'text', true],
    ['name', '氏名', 'text', true],
    ['relationToHead', '世帯主との続柄', 'text', true],
    ['birthDate', '生年月日', 'date', true],
    ['benefitPeriodStart', '生活保護受給期間（開始）', 'date', true],
    ['benefitPeriodEnd', '生活保護受給期間（終了）', 'date', true],
    ['death', '死亡（申出日時点）', 'checkbox', false],
  ];

  const bankFields: [string, string][] = [
    ['bankCode', '銀行番号'],
    ['bankBranchCode', '支店番号'],
  ];

  // 保存済み + 選択済みファイルを共通のチップ表示で描画する（添付書類・加算挙証で共用）。
  // 保存済みチップの削除：単一ファイル枠 or 残り1件なら whole-key 削除、複数なら index 単位削除。
  // 選択済みチップの削除：その File 1 件だけを取り除く。何も無ければ「未添付」。
  const renderAttachmentChips = (key: string) => {
    const multiFile = isMultiFileAttachmentKey(key);
    const attachment = form.attachments?.[key] as Parameters<typeof attachmentFilesOf>[0];
    const savedFiles = attachmentFilesOf(attachment);
    const deleteMarked = attachmentDeletes.has(key);
    const deletedIndexes = attachmentFileDeletes[key];
    const visibleSaved = deleteMarked
      ? []
      : savedFiles
          .map((file, idx) => ({ file, idx }))
          .filter(({ idx }) => !(deletedIndexes?.has(idx)));
    const selectedFiles = attachmentFiles[key] || [];
    const remainingSavedCount = currentAttachmentFileCount(key);
    const hasAny = visibleSaved.length > 0 || selectedFiles.length > 0;

    return (
      <>
        {visibleSaved.map(({ file, idx }) => (
          <span key={`saved-${key}-${idx}-${file.fileName}`} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 pl-2.5 pr-1 py-1 min-w-0 max-w-full">
            <span className="font-mono text-xs text-gray-600 min-w-0 max-w-[15rem] truncate" title={file.fileName}>{file.fileName}</span>
            <button
              type="button"
              onClick={() => {
                if (!multiFile || remainingSavedCount <= 1) markAttachmentDelete(key);
                else markSavedFileDelete(key, idx);
              }}
              className="shrink-0 ml-0.5 inline-flex items-center text-xs font-medium text-red-600 rounded px-1.5 py-0.5 hover:text-white hover:bg-red-500 transition cursor-pointer"
            >
              削除
            </button>
          </span>
        ))}
        {selectedFiles.map((file, idx) => (
          <span key={`selected-${key}-${idx}-${file.name}`} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 pl-2.5 pr-1 py-1 min-w-0 max-w-full">
            <span className="font-mono text-xs text-gray-600 min-w-0 max-w-[15rem] truncate" title={file.name}>{file.name}</span>
            <button
              type="button"
              onClick={() => removeSelectedAttachmentFileAt(key, idx)}
              className="shrink-0 ml-0.5 inline-flex items-center text-xs font-medium text-red-600 rounded px-1.5 py-0.5 hover:text-white hover:bg-red-500 transition cursor-pointer"
            >
              選択解除
            </button>
          </span>
        ))}
        {!hasAny ? <span className="text-xs text-gray-400">未添付</span> : null}
      </>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader breadcrumbs={[{ label: '受給者／申出書 検索', page: 'recipient-search' }, { label: '申出書詳細情報' }, { label: '編集' }]} />
        <div className="flex min-h-[420px] items-center justify-center">
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[
        { label: '受給者／申出書 検索', page: 'recipient-search' },
        ...(isCreateMode ? [] : [
          effectiveApplicationId
            ? { label: '申出書詳細情報', page: 'application-document-detail', navigateOpts: { applicationId: effectiveApplicationId } }
            : { label: '申出書詳細情報' },
        ]),
        { label: isCreateMode ? '申出書新規登録' : '編集' },
      ]} />
      <div className="max-w-5xl mx-auto px-6 py-8">
        {statusBlocksNonCommentEdit && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {`現在の案件ステータス「${form.caseStatus}」ではコメントのみ編集可能です。その他の項目は閲覧のみとなります。`}
          </div>
        )}
        <h2 className="text-xl font-bold text-gray-800 mb-5">{isCreateMode ? '申出書新規登録' : '申出書詳細情報 編集'}</h2>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6 mb-5">
          <SectionHeader title="コメント" />
          <Input.TextArea
            value={form.memo}
            onChange={(e) => updateField('memo', e.target.value)}
            rows={4}
          />
        </div>

        <fieldset disabled={commentOnly} className="contents">
        <Card variant="borderless" className={`mb-6 bg-transparent shadow-none${commentOnly ? ' opacity-60' : ''}`} styles={{ body: { padding: 0 } }}>
          {/* fieldset disabled は antd Select/Radio など独自クリックハンドラを持つ複合コンポーネントを
              無効化できないため、Form disabled で DisabledContext 経由でも伝播させる。 */}
          <Form layout="vertical" colon={false} disabled={commentOnly} className="space-y-5">
            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader title="申出者情報" />
              {/* 紙の申出書の表頭順（申出年月日→現住所→氏名→連絡先）に合わせて配置。
                  検討事項 No.66: 申出者は世帯構成1行目とは限らないため、氏名・フリガナ・生年月日は
                  世帯構成から独立した項目として入力する。世帯構成のどの行へ反映するかは
                  各行の「申出者情報を転記」ボタンで選ぶ。
                  識別ID・申請ID・世帯番号・ステータス等のシステム項目はプロトタイプ version2 に倣い非表示。 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                <Form.Item label={requiredLabel('申出年月日')}>
                  <Input type="date" min="1900-01-01" max="9999-12-31" value={form.applicationDate ?? ''} onChange={(e) => updateField('applicationDate', e.target.value)} />
                  {submitErrors.applicationDate && <div className="customer-inline-error">{submitErrors.applicationDate}</div>}
                </Form.Item>
                <Form.Item label={requiredLabel('現住所')} className="md:col-span-2">
                  <Input
                    value={form.currentAddress ?? ''}
                    onChange={(e) => updateField('currentAddress', e.target.value)}
                    placeholder="例：東京都千代田区霞が関1-2-3 サンプルマンション101"
                  />
                  {submitErrors.currentAddress && <div className="customer-inline-error">{submitErrors.currentAddress}</div>}
                </Form.Item>
                <Form.Item label={requiredLabel('（フリガナ）')}>
                  <NameSplitInput
                    value={form.applicantFurigana ?? ''}
                    onChange={(next) => updateField('applicantFurigana', next)}
                    lastLabel="セイ"
                    firstLabel="メイ"
                    lastPlaceholder="例：ヤマダ"
                    firstPlaceholder="例：タロウ"
                    lastAriaLabel="フリガナ セイ"
                    firstAriaLabel="フリガナ メイ"
                  />
                  {submitErrors.applicantFurigana && <div className="customer-inline-error">{submitErrors.applicantFurigana}</div>}
                </Form.Item>
                <Form.Item label={requiredLabel('代表者氏名（表示用）')}>
                  <NameSplitInput
                    value={form.name ?? ''}
                    onChange={(next) => updateField('name', next)}
                    lastLabel="姓"
                    firstLabel="名"
                    lastPlaceholder="例：山田"
                    firstPlaceholder="例：太郎"
                  />
                  {submitErrors.applicantName && <div className="customer-inline-error">{submitErrors.applicantName}</div>}
                </Form.Item>
                <Form.Item label={requiredLabel('生年月日')}>
                  <Input
                    type="date"
                    min="1900-01-01"
                    max="9999-12-31"
                    value={form.applicantBirthDate ?? ''}
                    onChange={(e) => updateField('applicantBirthDate', e.target.value)}
                  />
                  {submitErrors.applicantBirthDate && <div className="customer-inline-error">{submitErrors.applicantBirthDate}</div>}
                </Form.Item>
                <Form.Item label={requiredLabel('電話番号')}>
                  <Input
                    value={form.applicantPhone ?? ''}
                    onChange={(e) => updateField('applicantPhone', phoneDigitsOnly(e.target.value))}
                    inputMode="numeric"
                    placeholder="例：0312345678"
                    className="font-mono"
                  />
                  {submitErrors.applicantPhone && <div className="customer-inline-error">{submitErrors.applicantPhone}</div>}
                </Form.Item>
                <Form.Item
                  label={<span>メールアドレス<span className="ml-1 text-xs font-normal text-gray-400">（任意）</span></span>}
                  className="md:col-span-2"
                  extra="不備発生時の一次連絡をメールで受け取りたい方のみこちらにご記入ください。"
                >
                  <Input
                    type="email"
                    value={form.applicantEmail ?? ''}
                    onChange={(e) => updateField('applicantEmail', e.target.value)}
                    placeholder="例：sample@example.com"
                  />
                  {submitErrors.applicantEmail && <div className="customer-inline-error">{submitErrors.applicantEmail}</div>}
                </Form.Item>
                <p className="md:col-span-2 text-xs text-gray-500">※ 申出をされたご本人の情報を入力してください。ここで入力した氏名・フリガナ・生年月日は、「世帯構成」の該当する行の「申出者情報を転記」を押すとその行に反映されます。</p>
              </div>
            </section>

            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader badge="1" title="世帯構成（申出者及び世帯員　1～8名）" />
              <p className="mb-3 text-xs text-gray-500">保護を受給していた期間の世帯員すべてを記載してください。世帯主との続柄は各行で選択できます。申出日時点で死亡している場合は「死亡」にチェックしてください。</p>
              <div className="space-y-4">
                {form.members.map((m, mi) => (
                  <div key={mi} className="border border-gray-200 rounded-lg p-4 bg-gray-50/80">
                    <div className="flex justify-between items-center mb-3">
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        世帯員 {mi + 1}
                        <Button type="primary" size="small" onClick={() => applyApplicantToMember(mi)}>
                          申出者情報を転記
                        </Button>
                      </span>
                      {form.members.length > 1 ? (
                        <Button type="link" danger size="small" onClick={() => removeMember(mi)} className="!px-0">
                          この行を削除
                        </Button>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                      {memberFields.map(([k, lab, type, req]) => (
                        <Form.Item key={k} label={req ? requiredLabel(lab) : lab}>
                          {type === 'checkbox' ? (
                            <Checkbox
                              checked={m[k] === '有'}
                              onChange={(e) => updateMember(mi, k, e.target.checked ? '有' : '')}
                            />
                          ) : k === 'furigana' ? (
                            <NameSplitInput
                              value={m[k] ?? ''}
                              onChange={(next) => updateMember(mi, k, next)}
                              lastLabel="セイ"
                              firstLabel="メイ"
                              lastPlaceholder="例：ヤマダ"
                              firstPlaceholder="例：タロウ"
                              lastAriaLabel="フリガナ セイ"
                              firstAriaLabel="フリガナ メイ"
                            />
                          ) : k === 'name' ? (
                            <NameSplitInput
                              value={m[k] ?? ''}
                              onChange={(next) => updateMember(mi, k, next)}
                            />
                          ) : k === 'relationToHead' ? (
                            (
                              <>
                                <Select
                                  value={m.relationToHead || undefined}
                                  onChange={(val) => updateMemberRelation(mi, val)}
                                  placeholder="選択してください"
                                  options={APP_DOC_RELATION_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
                                />
                                {m.relationToHead === 'その他' && (
                                  <>
                                    <Input
                                      className="mt-2"
                                      value={m.relationToHeadOther ?? ''}
                                      onChange={(e) => updateMember(mi, 'relationToHeadOther', e.target.value)}
                                      placeholder="続柄（その他）の内容を入力"
                                      aria-label="続柄（その他）"
                                    />
                                    {submitErrors.members?.[mi]?.relationToHeadOther && (
                                      <div className="customer-inline-error">{submitErrors.members[mi].relationToHeadOther}</div>
                                    )}
                                  </>
                                )}
                              </>
                            )
                          ) : (
                            <Input
                              type={type}
                              {...(type === 'date' ? { min: '1900-01-01', max: '9999-12-31' } : {})}
                              value={m[k] ?? ''}
                              onChange={(e) => updateMember(mi, k, e.target.value)}
                              inputMode={k === 'phone' ? 'numeric' : undefined}
                              className={k === 'phone' ? 'font-mono' : undefined}
                            />
                          )}
                          {submitErrors.members?.[mi]?.[k] && (
                            <div className="customer-inline-error">{submitErrors.members[mi][k]}</div>
                          )}
                        </Form.Item>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {form.members.length < 8 ? (
                <button
                  type="button"
                  onClick={addMember}
                  disabled={commentOnly}
                  className="mt-3 inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-lg leading-none">＋</span> 世帯員を追加（最大8名）
                </button>
              ) : null}
            </section>

            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader badge="2" title="保護を受給していた当時の居所（選択肢をチェックしてください。）" />
              <Radio.Group
                value={form.protectionResidenceSame}
                onChange={(e) => {
                  if (e.target.value === 'same') {
                    setForm((prev) => ({ ...prev, protectionResidenceSame: 'same', pastAddresses: [] }));
                  } else {
                    setForm((prev) => ({
                      ...prev,
                      protectionResidenceSame: 'different',
                      pastAddresses: prev.pastAddresses.length ? prev.pastAddresses : [emptyPastAddress()],
                    }));
                  }
                }}
              >
                <Radio value="same">現住所と同じ</Radio>
                <Radio value="different">現住所と異なる</Radio>
              </Radio.Group>
              <p className="mt-2 text-xs text-gray-500">現住所と異なる場合は、以下の「当時の居所」及び「期間」を記載してください。</p>
            </section>

            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader title="当時の居所・期間（最大3件）" />
              {form.protectionResidenceSame === 'same' ? (
                <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  「2. 保護を受給していた当時の居所」が「現住所と同じ」のため、当時の居所は入力できません。
                </p>
              ) : (
                <>
                  <div className="space-y-3">
                    {form.pastAddresses.map((p, pi) => (
                      <div key={pi} className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-gray-700">#{pi + 1}</span>
                          <Button type="link" danger size="small" onClick={() => removePastAddress(pi)}>削除</Button>
                        </div>
                        <Form.Item label="当時の居所" className="mb-3">
                          <Input
                            value={p.address}
                            onChange={(e) => updatePast(pi, 'address', e.target.value)}
                            placeholder="当時住んでいた場所を記載"
                          />
                        </Form.Item>
                        <Form.Item label="期間" className="mb-0">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <Input
                              type="month"
                              value={p.periodStartMonth ?? ''}
                              onChange={(e) => updatePast(pi, 'periodStartMonth', e.target.value)}
                              aria-label="当時の居所 期間開始日"
                              className="sm:flex-1"
                            />
                            <span className="text-center text-gray-500">～</span>
                            <Input
                              type="month"
                              value={p.periodEndMonth ?? ''}
                              onChange={(e) => updatePast(pi, 'periodEndMonth', e.target.value)}
                              aria-label="当時の居所 期間終了日"
                              className="sm:flex-1"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">年月で入力してください。</p>
                        </Form.Item>
                      </div>
                    ))}
                  </div>
                  {form.pastAddresses.length < 3 ? (
                    <button
                      type="button"
                      onClick={addPastAddress}
                      disabled={commentOnly}
                      className="mt-3 inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-lg leading-none">＋</span> 行を追加
                    </button>
                  ) : null}
                </>
              )}
            </section>

            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader badge="3" title="振込先口座" />
              <p className="mb-3 text-xs text-gray-500">振込先は申出者本人名義の口座に限ります。口座名義は半角カタカナで入力してください。</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                {bankFields.map(([key, label]) => (
                  <Form.Item key={key} label={requiredLabel(label)}>
                    <Input
                      value={form.bank[key] ?? ''}
                      onChange={(e) => updateBank(key, e.target.value)}
                      inputMode="numeric"
                      maxLength={key === 'bankCode' ? 4 : 3}
                      className="font-mono"
                    />
                    {submitErrors.bank?.[key] && (
                      <div className="customer-inline-error">{submitErrors.bank[key]}</div>
                    )}
                  </Form.Item>
                ))}
                <Form.Item label={requiredLabel('預金種目')}>
                  <Select
                    value={form.bank.accountType || undefined}
                    onChange={(val) => updateBank('accountType', val)}
                    placeholder="選択してください"
                    options={APPLICATION_BANK_ACCOUNT_TYPE_OPTIONS}
                  />
                  {submitErrors.bank?.accountType && (
                    <div className="customer-inline-error">{submitErrors.bank.accountType}</div>
                  )}
                </Form.Item>
                <Form.Item label={requiredLabel('口座番号')}>
                  <Input
                    value={form.bank.accountNumber ?? ''}
                    onChange={(e) => updateBank('accountNumber', e.target.value)}
                    inputMode="numeric"
                    maxLength={7}
                    className="font-mono"
                  />
                  {submitErrors.bank?.accountNumber && (
                    <div className="customer-inline-error">{submitErrors.bank.accountNumber}</div>
                  )}
                </Form.Item>
                <Form.Item label={<span>{requiredLabel('口座名義')}<span className="ml-1 text-xs font-normal text-gray-400">（半角カタカナ・全角は自動変換）</span></span>}>
                  <Input
                    value={form.bank.accountHolderKana ?? ''}
                    onChange={(e) => updateBank('accountHolderKana', e.target.value, Boolean((e.nativeEvent as { isComposing?: boolean }).isComposing))}
                    onCompositionEnd={(e) => updateBank('accountHolderKana', e.currentTarget.value)}
                    placeholder="例：ﾔﾏﾀﾞ ﾀﾛｳ（ヤマダ タロウ と入力しても自動変換）"
                    className="font-mono"
                  />
                  {submitErrors.bank?.accountHolderKana && (
                    <div className="customer-inline-error">{submitErrors.bank.accountHolderKana}</div>
                  )}
                </Form.Item>
              </div>
            </section>

            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader badge="4" title="申出者以外の世帯員の連絡先（複数人世帯の場合）" />
              <p className="mb-3 text-xs text-gray-500">申出者へ連絡がつかないときの連絡先（他の世帯員）を記載してください。最大2件。</p>
              {form.otherHouseholdMembers.length === 0 ? (
                <p className="text-sm text-gray-500 mb-2">未登録です。必要に応じて行を追加してください。</p>
              ) : (
                <div className="space-y-3">
                  {form.otherHouseholdMembers.map((o, oi) => (
                    <div key={oi} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-x-4 gap-y-2 border border-gray-200 rounded-lg p-4 bg-gray-50/80 items-start">
                      <Form.Item label="他の世帯員の氏名" className="mb-0">
                        <Input value={o.name} onChange={(e) => updateOther(oi, 'name', e.target.value)} placeholder="例：山田 花子" />
                      </Form.Item>
                      <Form.Item label="電話番号（携帯または自宅）" className="mb-0">
                        <Input value={o.phone} onChange={(e) => updateOther(oi, 'phone', e.target.value)} inputMode="numeric" placeholder="例：09012345678" className="font-mono" />
                        {submitErrors.otherHouseholdMembers?.[oi]?.phone && (
                          <div className="customer-inline-error">{submitErrors.otherHouseholdMembers[oi].phone}</div>
                        )}
                      </Form.Item>
                      <div className="flex items-end justify-end h-full pb-1">
                        <Button type="link" danger size="small" onClick={() => removeOtherMember(oi)} className="!px-0">削除</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={addOtherMember}
                disabled={commentOnly || form.otherHouseholdMembers.length >= 2}
                className="mt-3 inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-lg leading-none">＋</span> 連絡先を追加（最大2件）
              </button>
            </section>

            <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6">
              <SectionHeader badge="5" title="加算等の申出" />
              <p className="mb-3 text-xs text-gray-500">申出者及び世帯員について、該当する加算等を記載してください（該当がない場合は記載不要）。該当がある場合は併せて挙証資料を添付してください。</p>
              {form.additionRows.length === 0 ? (
                <p className="text-sm text-gray-500 mb-2">該当なし。必要に応じて行を追加してください。</p>
              ) : (
                <div className="space-y-3">
                  {form.additionRows.map((a, ai) => {
                    const meta = appDocAddonMeta(a.category);
                    // 行に紐づく安定キー（evidenceKey）を表示/削除の対象キーにする。
                    // 旧データは index 無しの 'addonEvidence' や addonEvidence_<index> のことがあり、
                    // いずれもバックエンドが evidenceKey として返すためそのまま使える。未採番の新規行は index 仮キー。
                    const evidenceUploadKey = a.evidenceKey && String(a.evidenceKey).trim()
                      ? String(a.evidenceKey)
                      : `addonEvidence_${ai}`;
                    return (
                      <div key={ai} className="border border-gray-200 rounded-lg p-4 bg-gray-50/60">
                        <div className="mb-3 flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">加算等 {ai + 1}</span>
                          <Button type="link" danger size="small" onClick={() => removeAdditionRow(ai)} className="!px-0">この行を削除</Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                          <Form.Item label="加算等の種類">
                            <Select value={a.category} onChange={(val) => updateAddition(ai, 'category', val)} options={APP_DOC_ADDON_TYPES.map((t) => ({ value: t, label: t }))} />
                          </Form.Item>
                          <Form.Item label="氏名">
                            <Input value={a.name ?? ''} onChange={(e) => updateAddition(ai, 'name', e.target.value)} placeholder="該当者の氏名" />
                          </Form.Item>
                          <Form.Item label="期間" className="md:col-span-2">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                              <Input type="month" value={a.periodStartMonth ?? ''} onChange={(e) => updateAddition(ai, 'periodStartMonth', e.target.value)} aria-label="加算等 期間開始" className="sm:flex-1" />
                              <span className="text-center text-gray-500">～</span>
                              <Input type="month" value={a.periodEndMonth ?? ''} onChange={(e) => updateAddition(ai, 'periodEndMonth', e.target.value)} aria-label="加算等 期間終了" className="sm:flex-1" />
                            </div>
                            {meta.note ? <p className="mt-1 text-xs text-gray-500">{meta.note}</p> : null}
                          </Form.Item>
                          {meta.extraLabel ? (
                            <Form.Item label={meta.extraLabel} className="md:col-span-2">
                              <Input value={a.facility ?? ''} onChange={(e) => updateAddition(ai, 'facility', e.target.value)} placeholder={meta.extraLabel} />
                            </Form.Item>
                          ) : null}
                        </div>
                        {meta.evidence ? (
                          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
                            <p className="text-xs text-gray-700 leading-relaxed"><span className="font-bold text-amber-700">挙証資料の例：</span>{meta.evidence}</p>
                          </div>
                        ) : null}
                        <div className="flex flex-col items-start gap-2">
                          {renderAttachmentChips(evidenceUploadKey)}
                          <label className="shrink-0 cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-blue-500 text-blue-700 hover:bg-blue-50 transition">
                            📎 挙証資料を添付
                            <input
                              type="file"
                              accept={ATTACHMENT_FILE_ACCEPT}
                              multiple
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                selectAddonEvidence(ai, e.target.files);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={addAdditionRow}
                disabled={commentOnly}
                className="mt-3 inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-lg leading-none">＋</span> 加算等を追加
              </button>
            </section>

            <section className={`bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 p-5 md:p-6${submitErrors.attachments ? ' ring-red-300 bg-red-50/40' : ''}`}>
              <SectionHeader title="添付書類（証明書等）" />
              <p className="text-sm text-gray-600 mb-4">
                「ファイルを選択」でファイルを追加・差し替えできます。
              </p>
              {submitErrors.attachments ? (
                <div className="customer-inline-error mb-3">{submitErrors.attachments}</div>
              ) : null}
              <div className="space-y-3">
                {APP_DOC_ATTACHMENT_GROUPS.map((group, gi) => {
                  const firstIdDoc = group.idDoc && (gi === 0 || !APP_DOC_ATTACHMENT_GROUPS[gi - 1]?.idDoc);
                  // legacyOnly（マイナンバー裏面）は既存添付があるときのみ表示（閲覧・削除用）
                  const parts = group.parts.filter(
                    (part) => !part.legacyOnly || attachmentFilesOf(form.attachments?.[part.key] as Parameters<typeof attachmentFilesOf>[0]).length > 0,
                  );
                  return (
                    <div key={group.label}>
                      {firstIdDoc ? (
                        <p className="text-xs font-semibold text-gray-500 mb-2">本人確認書類（いずれか1点）</p>
                      ) : null}
                      <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-3.5">
                        <p className="text-sm font-semibold text-gray-800 leading-relaxed">{group.label}</p>
                        {group.note ? <p className="text-xs text-gray-500 mt-1 leading-relaxed whitespace-pre-line">{group.note}</p> : null}
                        <div className={`mt-2 grid gap-3 ${parts.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                          {parts.map((part) => {
                            const multiFile = isMultiFileAttachmentKey(part.key);
                            const missing = submitErrors.missingAttachmentKeys?.includes(part.key) ?? false;
                            return (
                              <div key={part.key} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                                {part.label ? <p className="text-xs text-gray-500 mb-1.5">{part.label}</p> : null}
                                <div className="flex flex-col items-start gap-2">
                                  {renderAttachmentChips(part.key)}
                                  {part.legacyOnly ? (
                                    <p className="text-xs text-gray-500">※ 新規アップロードはできません（削除のみ可能です）。</p>
                                  ) : (
                                    <label className="shrink-0 cursor-pointer inline-flex items-center px-3 py-1.5 text-sm rounded-lg border border-blue-500 text-blue-700 hover:bg-blue-50 transition">
                                      ファイルを選択
                                      <input
                                        type="file"
                                        accept={ATTACHMENT_FILE_ACCEPT}
                                        multiple={multiFile}
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                          selectAttachmentFiles(part.key, e.target.files);
                                          e.target.value = '';
                                        }}
                                      />
                                    </label>
                                  )}
                                </div>
                                {missing && ATTACHMENT_REQUIRED_MESSAGES[part.key] ? (
                                  <span className="customer-inline-error block mt-1.5">{ATTACHMENT_REQUIRED_MESSAGES[part.key]}</span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </Form>
        </Card>
        </fieldset>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={saving}
            className="px-5 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={() => void saveAndBack()}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '保存中...' : isCreateMode ? '登録' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDocEdit;
